import axios, { type AxiosResponse, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import applyCaseMiddleware from 'axios-case-converter';

import { removeItems, setItems } from './local-storage';
import { API_URL, REFRESH_TOKEN_KEY, TOKEN_KEY, SOURCE_HEADERS } from './constants';
import AppRoutes from './app-routes';
import { sanitizeToISO88591 } from './helpers';

export const http = applyCaseMiddleware(
	axios.create({
		baseURL: API_URL,
		headers: {
			Vary: '*',
			Accept: 'application/json'
		}
	})
);

export const post = async (endpoint: string, data?: unknown): Promise<AxiosResponse> => {
	return await new Promise((resolve, reject) => {
		http.post(endpoint, data)
			.then(resolve)
			.catch(e => {
				reject(e?.response?.data);
			});
	});
};

/** POST that returns response body as Blob (e.g. file download). Use for endpoints that return binary (XLS, PDF, etc.). */
export const postBlob = async (endpoint: string, data?: unknown): Promise<AxiosResponse<Blob>> => {
	return await new Promise((resolve, reject) => {
		http
			.post(endpoint, data, { responseType: 'blob' })
			.then(resolve)
			.catch(e => {
				reject(e?.response ?? e);
			});
	});
};

export const get = async (endpoint: string, queryParams?: Record<string, unknown>): Promise<AxiosResponse> => {
	let config: AxiosRequestConfig = {
		data: {}
	};
	if (queryParams) {
		config = { ...config, params: queryParams };
	}
	return await new Promise((resolve, reject) => {
		http.get(endpoint, config)
			.then(resolve)
			.catch(e => {
				reject(e.response?.data);
			});
	});
};

/** GET that returns response body as Blob (e.g. file download). Use for endpoints that return binary. */
export const getBlob = async (
	endpoint: string,
	queryParams?: Record<string, unknown>
): Promise<AxiosResponse<Blob>> => {
	let config: AxiosRequestConfig = { responseType: 'blob' };
	if (queryParams) {
		config = { ...config, params: queryParams };
	}
	return await new Promise((resolve, reject) => {
		http.get(endpoint, config)
			.then(resolve)
			.catch(e => {
				reject(e?.response ?? e);
			});
	});
};

export const patch = async <T>(endpoint: string, data: T): Promise<AxiosResponse> =>
	await new Promise((resolve, reject) => {
		http.patch(endpoint, data)
			.then(resolve)
			.catch(e => {
				reject(e.response?.data);
			});
	});
export const put = async <T>(endpoint: string, data?: T): Promise<AxiosResponse> =>
	await new Promise((resolve, reject) => {
		http.put(endpoint, data)
			.then(resolve)
			.catch(e => {
				reject(e?.response?.data ?? e);
			});
	});

export const del = async (endpoint: string): Promise<AxiosResponse> =>
	await new Promise((resolve, reject) => {
		http.delete(endpoint)
			.then(resolve)
			.catch(e => {
				reject(e?.response?.data ?? e);
			});
	});

export const postWithoutToken = async (endpoint: any, data?: any, config?: any): Promise<AxiosResponse> => {
	let headers = {};
	if (config?.addSourceHeader) {
		headers = { ...headers, [SOURCE_HEADERS[0]]: SOURCE_HEADERS[1] };
	}
	return await axios.post(API_URL + '/' + endpoint, data, {
		headers: { ...headers }
	});
};
export const fileUploadWithoutToken = async (endpoint: any, file: File, config?: any): Promise<AxiosResponse> => {
	let headers: any = {
		'Content-Type': file.type
	};
	if (config?.headers && config.headers.file !== '') {
		headers = {
			...headers,
			'x-amz-meta-filename': sanitizeToISO88591(config.headers.file)
		};
	}

	return await new Promise((resolve, reject) => {
		axios
			.put(endpoint, file, {
				headers: { ...headers }
			})
			.then(resolve)
			.catch(e => {
				reject(e);
			});
	});
};

export const fetchHeadObjFromURL = async (endpoint: any) => {
	return await new Promise((resolve, reject) => {
		axios
			.get(endpoint, {
				headers: {
					Range: 'bytes=0-0'
				}
			})
			.then(res => {
				resolve(res);
			})
			.catch(e => {
				reject(e);
			});
	});
};

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

type QueuedRequest = {
	resolve: (value: AxiosResponse | Promise<AxiosResponse>) => void;
	reject: (reason?: unknown) => void;
	config: RetryConfig;
};

let isRefreshing = false;
let refreshQueue: QueuedRequest[] = [];
let refreshPromise: Promise<string> | null = null;

const forceLogin = () => {
	removeItems();
	if (window.location.pathname !== AppRoutes.LOGIN) {
		window.location.href = AppRoutes.LOGIN;
	}
};

const flushQueue = (error: unknown, accessToken?: string) => {
	const queued = refreshQueue;
	refreshQueue = [];
	queued.forEach(({ resolve, reject, config }) => {
		if (error || !accessToken) {
			reject(error);
			return;
		}
		if (config.headers) {
			config.headers.Authorization = `Bearer ${accessToken}`;
		}
		resolve(http(config));
	});
};

/** Single-flight refresh with optional cross-tab lock (Web Locks API). */
const refreshAccessToken = async (): Promise<string> => {
	if (refreshPromise) {
		return refreshPromise;
	}

	const runRefresh = async (): Promise<string> => {
		const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
		if (!refreshToken) {
			throw new Error('Missing refresh token');
		}

		const res = await postWithoutToken(
			'auth/refresh-token',
			{ token: refreshToken, refresh_token: refreshToken },
			{ addSourceHeader: true }
		);

		const authResponse = res.data?.auth_response ?? res.data;
		if (!authResponse?.access_token && !authResponse?.accessToken) {
			throw new Error('Refresh response missing access token');
		}

		// Normalize to the shape setItems expects (snake_case).
		const normalized = {
			access_token: authResponse.access_token ?? authResponse.accessToken,
			refresh_token: authResponse.refresh_token ?? authResponse.refreshToken,
			expiry_time: authResponse.expiry_time ?? authResponse.expiryTime,
			expires_in: authResponse.expires_in ?? authResponse.expiresIn,
			user_type: authResponse.user_type ?? authResponse.userType
		};
		setItems(normalized);
		return normalized.access_token as string;
	};

	const withLock = async (): Promise<string> => {
		const locks = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;
		if (!locks?.request) {
			return runRefresh();
		}
		return locks.request('hk-erp-refresh-token', async () => {
			// Another tab may have refreshed while we waited for the lock.
			const existing = localStorage.getItem(TOKEN_KEY);
			const threshold = Number(localStorage.getItem('access-token-threshold') || 0);
			const nowSec = Math.floor(Date.now() / 1000);
			if (existing && threshold > nowSec + 60) {
				return existing;
			}
			return runRefresh();
		});
	};

	refreshPromise = withLock()
		.then(token => {
			flushQueue(null, token);
			return token;
		})
		.catch(error => {
			flushQueue(error);
			forceLogin();
			throw error;
		})
		.finally(() => {
			isRefreshing = false;
			refreshPromise = null;
		});

	isRefreshing = true;
	return refreshPromise;
};

http.interceptors.request.use(
	(config: InternalAxiosRequestConfig) => {
		config.headers = config.headers ?? {};
		config.headers.Authorization = 'Bearer ' + (localStorage.getItem(TOKEN_KEY) || '');
		return config;
	},
	async (error: any) => await Promise.reject(error)
);

http.interceptors.response.use(
	(response: AxiosResponse) => response,
	async (error: any) => {
		const status = error?.response?.status;
		const originalRequest = error?.config as RetryConfig | undefined;

		if (status === 403 && error?.response?.data?.code == 'FIRST_LOGIN_ERROR') {
			forceLogin();
			return Promise.reject(error);
		}

		// Only attempt session recovery for expired/unauthorized access tokens.
		if (status !== 401 || !originalRequest) {
			return Promise.reject(error);
		}

		if (originalRequest._retry) {
			forceLogin();
			return Promise.reject(error);
		}

		const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
		if (!refreshToken) {
			forceLogin();
			return Promise.reject(error);
		}

		originalRequest._retry = true;

		if (isRefreshing) {
			return new Promise((resolve, reject) => {
				refreshQueue.push({ resolve, reject, config: originalRequest });
			});
		}

		try {
			const accessToken = await refreshAccessToken();
			if (originalRequest.headers) {
				originalRequest.headers.Authorization = `Bearer ${accessToken}`;
			}
			return await http(originalRequest);
		} catch (refreshError) {
			return Promise.reject(refreshError);
		}
	}
);
